import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "../UIElements/Icon";
import { NativeSelect } from "../UIElements/Select";
import { useBusinessCategories } from "../../../hooks/useBusinessCatalog";

const CategoryRuleCheckbox = ({
  checked,
  label,
  onToggle,
  variant,
  children,
}) => {
  const isChecked = Boolean(checked);
  const handleToggle = () => {
    if (!onToggle) {
      return;
    }
    onToggle();
  };

  const baseColorClasses =
    variant === "exclude"
      ? "border-destructive/50 data-[checked=true]:bg-destructive data-[checked=true]:border-destructive"
      : "border-primary/50 data-[checked=true]:bg-primary data-[checked=true]:border-primary";

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isChecked}
      aria-label={label}
      tabIndex={0}
      data-checked={isChecked}
      onClick={handleToggle}
      className="hover:bg-accent focus-visible:ring-ring flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded px-2 text-start transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    >
      <span
        aria-hidden="true"
        data-checked={isChecked}
        className={`bg-background flex h-5 w-5 shrink-0 items-center justify-center rounded border ${baseColorClasses}`}
      >
        {isChecked ? (
          <Icon name="Check" className="h-3.5 w-3.5 text-white" />
        ) : null}
      </span>
      <span className="text-foreground text-sm leading-5 whitespace-nowrap">
        {children}
      </span>
    </button>
  );
};

const normalizeRulesCategoriesValue = (value) => {
  if (!Array.isArray(value)) {
    return new Map();
  }

  const map = new Map();

  value.forEach((item) => {
    if (typeof item === "number") {
      map.set(item, true);
      return;
    }

    if (!item || typeof item !== "object") {
      return;
    }

    const id = Number(item.id);
    if (!Number.isFinite(id)) {
      return;
    }

    const includeFromItem =
      typeof item.include === "boolean"
        ? item.include
        : typeof item?.pivot?.include === "boolean"
          ? item.pivot.include
          : true;

    map.set(id, includeFromItem);
  });

  return map;
};

const buildRulesCategoriesPayload = (selectionMap) => {
  const entries = Array.from(selectionMap.entries());

  if (entries.length === 0) {
    return null;
  }

  return entries.map(([id, include]) => ({
    id,
    include,
    is_condition: true,
  }));
};

const buildCategoryTree = (categories) => {
  const safe = Array.isArray(categories) ? categories : [];

  const toNode = (category) => {
    const id = Number(category?.id);
    const enabled = category?.enabled;

    if (!Number.isFinite(id)) {
      return null;
    }

    const children = Array.isArray(category?.subcategories)
      ? category.subcategories.map(toNode).filter(Boolean)
      : [];

    return {
      id,
      name: String(category?.name || `Category ${id}`),
      enabled: enabled !== false,
      children,
    };
  };

  return safe.map(toNode).filter(Boolean);
};

const collectAllNodeIds = (nodes, set) => {
  nodes.forEach((node) => {
    set.add(node.id);
    if (Array.isArray(node.children) && node.children.length > 0) {
      collectAllNodeIds(node.children, set);
    }
  });
};

const filterTreeBySearch = (nodes, query) => {
  if (!query) {
    return nodes;
  }

  const lower = query.toLowerCase();

  const filterNode = (node) => {
    const nameMatch = node.name.toLowerCase().includes(lower);
    const filteredChildren = node.children.map(filterNode).filter(Boolean);

    if (nameMatch || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren,
      };
    }

    return null;
  };

  return nodes.map(filterNode).filter(Boolean);
};

const flattenTree = (nodes, expandedSet, depth = 0) => {
  const result = [];

  nodes.forEach((node) => {
    result.push({ node, depth });

    if (node.children.length === 0) {
      return;
    }

    if (!expandedSet.has(node.id)) {
      return;
    }

    result.push(...flattenTree(node.children, expandedSet, depth + 1));
  });

  return result;
};

const PromotionRulesCategories = ({
  selectedBusinessIds,
  businesses,
  value,
  onChange,
}) => {
  const eligibleBusinesses = useMemo(() => {
    const ids = new Set(
      Array.isArray(selectedBusinessIds)
        ? selectedBusinessIds.map((id) => Number(id))
        : [],
    );

    const list = Array.isArray(businesses) ? businesses : [];

    return list
      .filter((business) => ids.has(Number(business?.id)))
      .map((business) => ({
        id: Number(business.id),
        name: String(business.name || `Business ${business.id}`),
      }))
      .filter((business) => Number.isFinite(business.id));
  }, [businesses, selectedBusinessIds]);

  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (eligibleBusinesses.length === 0) {
      setSelectedBusinessId("");
      return;
    }

    if (
      eligibleBusinesses.some(
        (b) => String(b.id) === String(selectedBusinessId),
      )
    ) {
      return;
    }

    setSelectedBusinessId(String(eligibleBusinesses[0].id));
  }, [eligibleBusinesses, selectedBusinessId]);

  const { data: categoriesData, isLoading } = useBusinessCategories(
    selectedBusinessId,
    {
      enabled: Boolean(selectedBusinessId),
    },
  );

  const categoryTree = useMemo(
    () => buildCategoryTree(categoriesData),
    [categoriesData],
  );
  const filteredTree = useMemo(
    () => filterTreeBySearch(categoryTree, search.trim()),
    [categoryTree, search],
  );

  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    const next = new Set();
    collectAllNodeIds(filteredTree, next);
    setExpandedIds(next);
  }, [selectedBusinessId, search, filteredTree]);

  const selectionMap = useMemo(
    () => normalizeRulesCategoriesValue(value),
    [value],
  );

  const setCategorySelection = (categoryId, nextInclude) => {
    const nextMap = new Map(selectionMap);

    if (nextInclude === null) {
      nextMap.delete(categoryId);
    } else {
      nextMap.set(categoryId, nextInclude);
    }

    const nextPayload = buildRulesCategoriesPayload(nextMap);
    if (onChange) {
      onChange(nextPayload);
    }
  };

  const toggleExpanded = (categoryId) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const rows = useMemo(
    () => flattenTree(filteredTree, expandedIds),
    [filteredTree, expandedIds],
  );

  if (!Array.isArray(selectedBusinessIds) || selectedBusinessIds.length === 0) {
    return (
      <div className="text-muted-foreground text-sm">
        Select at least one store in the promotion to configure category rules.
      </div>
    );
  }

  return (
    <div className="ordering-discounts-rule-picker space-y-4">
      <div className="space-y-2">
        <label className="text-muted-foreground block text-[length:var(--font-size-product-ui)] font-medium">
          Business
        </label>
        <div className="relative">
          <NativeSelect
            value={selectedBusinessId}
            onChange={(event) => setSelectedBusinessId(event.target.value)}
            className="border-border bg-background focus:ring-ring text-foreground w-full cursor-pointer appearance-none rounded-lg border py-2.5 pr-10 pl-4 transition-all outline-none focus:border-transparent focus:ring-2"
            aria-label="Select business for categories"
          >
            {eligibleBusinesses.map((business) => (
              <option key={business.id} value={business.id}>
                {business.name}
              </option>
            ))}
          </NativeSelect>
          <Icon
            name="ChevronDown"
            size={16}
            className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <label className="text-muted-foreground mb-1 block text-[length:var(--font-size-product-ui)] font-medium">
            Search categories
          </label>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type to filter…"
            className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 transition-all outline-none focus:border-transparent focus:ring-2"
            aria-label="Search categories"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-2">
          <h4 className="text-foreground text-sm font-semibold">
            Choose category to include
          </h4>
          <div className="border-border bg-background/60 max-h-[46vh] overflow-x-auto overflow-y-auto rounded-lg border p-2">
            {isLoading && (
              <p className="text-muted-foreground p-2 text-xs">
                Loading categories...
              </p>
            )}
            {!isLoading && rows.length === 0 && (
              <p className="text-muted-foreground p-2 text-xs">
                No categories found.
              </p>
            )}
            {!isLoading &&
              rows.map(({ node, depth }) => {
                if (!node.enabled) {
                  return null;
                }

                const isSelected = selectionMap.get(node.id) === true;
                const hasChildren = node.children.length > 0;
                const isExpanded = expandedIds.has(node.id);

                return (
                  <div
                    key={`include-${node.id}`}
                    className="flex min-w-max items-center gap-2 rounded py-1.5"
                    style={{ paddingLeft: `${8 + depth * 16}px` }}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(node.id)}
                        className="text-muted-foreground hover:bg-card flex h-6 w-6 items-center justify-center rounded"
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${
                          node.name
                        }`}
                      >
                        <Icon
                          name="ChevronDown"
                          className={`h-4 w-4 transition-transform ${
                            isExpanded ? "" : "-rotate-90"
                          }`}
                        />
                      </button>
                    ) : (
                      <span className="w-6" />
                    )}

                    <CategoryRuleCheckbox
                      checked={isSelected}
                      variant="include"
                      label={`Toggle include ${node.name}`}
                      onToggle={() =>
                        setCategorySelection(node.id, isSelected ? null : true)
                      }
                    >
                      {node.name}
                    </CategoryRuleCheckbox>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-foreground text-sm font-semibold">
            Choose category to exclude
          </h4>
          <div className="border-border bg-background/60 max-h-[46vh] overflow-x-auto overflow-y-auto rounded-lg border p-2">
            {isLoading && (
              <p className="text-muted-foreground p-2 text-xs">
                Loading categories...
              </p>
            )}
            {!isLoading && rows.length === 0 && (
              <p className="text-muted-foreground p-2 text-xs">
                No categories found.
              </p>
            )}
            {!isLoading &&
              rows.map(({ node, depth }) => {
                if (!node.enabled) {
                  return null;
                }

                const isSelected = selectionMap.get(node.id) === false;
                const hasChildren = node.children.length > 0;
                const isExpanded = expandedIds.has(node.id);

                return (
                  <div
                    key={`exclude-${node.id}`}
                    className="flex min-w-max items-center gap-2 rounded py-1.5"
                    style={{ paddingLeft: `${8 + depth * 16}px` }}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(node.id)}
                        className="text-muted-foreground hover:bg-card flex h-6 w-6 items-center justify-center rounded"
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${
                          node.name
                        }`}
                      >
                        <Icon
                          name="ChevronDown"
                          className={`h-4 w-4 transition-transform ${
                            isExpanded ? "" : "-rotate-90"
                          }`}
                        />
                      </button>
                    ) : (
                      <span className="w-6" />
                    )}

                    <CategoryRuleCheckbox
                      checked={isSelected}
                      variant="exclude"
                      label={`Toggle exclude ${node.name}`}
                      onToggle={() =>
                        setCategorySelection(node.id, isSelected ? null : false)
                      }
                    >
                      {node.name}
                    </CategoryRuleCheckbox>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <p className="text-muted-foreground text-xs">
        Selecting a category in “Include” sets{" "}
        <span className="text-muted-foreground">include=true</span>. Selecting
        in “Exclude” sets{" "}
        <span className="text-muted-foreground">include=false</span>. Unchecking
        removes it from the rule.
      </p>
    </div>
  );
};

export default PromotionRulesCategories;
